<template>
  <div class="package-management container">
    <div class="d-flex justify-content-between align-items-center my-8">
      <div>
        <h4 class="text-dark fw-bold mb-2">Quản lý Gói dịch vụ</h4>
        <p class="text-muted m-0">Quản lý các gói dịch vụ tổng hợp</p>
      </div>
      <div v-if="isInsertPermission" @click="createNewPackage" class="btn btn-success font-weight-medium">
        <i class="fas fa-plus me-2"></i>Tạo gói mới
      </div>
    </div>

    <div class="row mb-6 bg-white rounded border filter-bar">
      <div class="col-md-2 filter-col-input">
        <div class="position-relative">
          <i class="fas fa-search position-absolute search-icon"></i>
          <b-form-input
            v-model="searchQuery"
            placeholder="Tìm kiếm gói..."
            class="search-input"
            @input="handleSearchDebounce"
          />
        </div>
      </div>
      <div class="col-md-2">
        <basic-select
          label=""
          placeholder="Tất cả trạng thái"
          name="statusFilter"
          :options="packageStatusOptions"
          track-by="value"
          value-label="text"
          :value.sync="packageStatusValue"
          @input="statusFilter = $event != null ? $event : 'all'; handleSearch()"
        />
      </div>
      <div class="col-md-2">
        <basic-select
          label=""
          placeholder="Sắp xếp"
          name="sortBy"
          :options="sortByOptions"
          track-by="value"
          value-label="text"
          :value.sync="sortByValue"
          @input="sortBy = $event != null ? $event : ''; handleSearch()"
        />
      </div>
      <div class="col-md-2 filter-col-input d-flex align-items-center">
        <button class="btn btn-secondary btn-reset-filter w-100" @click="resetFilters">
          Reset bộ lọc
        </button>
      </div>
    </div>

    <div class="mb-6">
      <span class="text-muted">Hiện thị {{ packages.length }} gói dịch vụ</span>
    </div>

    <b-overlay :show="loading">
      <div class="row">
        <div class="col-xl-3 col-lg-6 col-md-6 mb-6" v-for="(pkg, index) in packages" :key="pkg.id || index">
          <div class="card package-card h-100">
            <div class="card-body p-6">
              <div class="d-flex justify-content-between align-items-start mb-4">
            <span 
              :class="{
                'badge-light-secondary': pkg.status === 0,
                'badge-light-success': pkg.status === 1,
                'badge-light-danger': pkg.status === 2
              }" 
              class="badge badge-circle">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-box-seam" viewBox="0 0 16 16">
                <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5l2.404.961L10.404 2zm3.564 1.426L5.596 5 8 5.961 14.154 3.5zm3.25 1.7-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z"/>
              </svg>
              <span 
                  :class="{
                    'status-draft':    pkg.status === 0,
                    'status-active':   pkg.status === 1,
                    'status-inactive': pkg.status === 2,
                  }"
                  class="status-badge"
                >
                {{ pkg.status === 0 ? 'Nháp' : pkg.status === 1 ? 'Hoạt động' : 'Không hoạt động' }}
              </span>
            </span>
          </div>
              <h3 class="card-title text-dark fw-bolder mb-3">{{ pkg.name }}</h3>

              <p class="text-muted mb-4">{{ pkg.description }}</p>
              <div class="price-section mb-4">
                <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                  <h4 class="price-current text-success fw-bolder fs-2 mb-0">
                    {{ formatPrice(pkg.salePrice > 0 ? pkg.salePrice : pkg.price) }} VNĐ
                  </h4>
                  <span class="price-original text-muted ">
                    {{ formatPrice(pkg.price) }} VNĐ
                  </span>
                  <span
                    v-if="pkg.salePrice > 0 && pkg.salePrice < pkg.price"
                    class="discount-badge badge badge-light-danger px-2 py-1"
                  >
                    -{{ calcDiscount(pkg.price, pkg.salePrice) }}%
                  </span>
                </div>
              </div>
              <div class="package-stats mb-4">
                <div class="row text-center">
                  <div class="col-4">
                    <div class="stat-item p-2 bg-light rounded">
                      <h5 class="fw-bold text-dark mb-1">{{ pkg.medicineCount }}</h5>
                      <span class="text-muted">Thuốc</span>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="stat-item p-2 bg-light rounded">
                      <h5 class="fw-bold text-dark mb-1">{{ pkg.servicePackageCount }}</h5>
                      <span class="text-muted">CT</span>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="stat-item p-2 bg-light rounded">
                      <h5 class="fw-bold text-dark mb-1">{{ pkg.serviceCount }}</h5>
                      <span class="text-muted">Dịch vụ</span>
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-muted mb-4">Thời hạn: {{ pkg.duration }} tháng | Tạo: {{ formatDate(pkg.createDatetime) }}</p>

              <div class="d-flex gap-3">
                <button class="btn btn-secondary" @click="viewPackage(pkg)">
                  <i class="far fa-eye me-2"></i>Xem
                </button>
                <b-button v-if="isUpdatePermission" variant="success" class="ml-4 btn btn-success" @click="editPackage(pkg)">
                  <i class="far fa-edit me-2"></i>Sửa
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="paging.total > 0" class="d-flex justify-content-between align-items-center mt-4">
        <div class="d-flex align-items-center">
          <b-form-select
            v-model="paging.pageSize"
            :options="pageSizeOptions"
            size="sm"
            class="page-size-select"
            style="width: 80px;"
          ></b-form-select>
        </div>
        <b-pagination
          v-model="paging.page"
          :total-rows="paging.total"
          :per-page="paging.pageSize"
          size="md"
          class="mb-0 pagination-custom"
          first-number
          last-number
        ></b-pagination>
      </div>
    </b-overlay>
  </div>
</template>

<script>
export default {
  name: 'PackageManagement',
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      sortBy: '',
      packages: [],
      loading: false,
      searchTimer: null,
      packageStatusOptions: [
        { value: 'all', text: 'Tất cả trạng thái' },
        { value: 'active', text: 'Hoạt động' },
        { value: 'inactive', text: 'Không hoạt động' },
        { value: 'draft', text: 'Nháp' },
      ],
      sortByOptions: [
        { value: '0', text: 'Mới nhất' },
        { value: '1', text: 'Từ A → Z' },
        { value: '2', text: 'Giá cao nhất' },
      ],
      paging: {
        page: 1,
        pageSize: 12,
        total: 0,
      },
      pageSizeOptions: [
        { value: 8,  text: '8' },
        { value: 12, text: '12' },
        { value: 24, text: '24' },
        { value: 48, text: '48' },
      ],
    };
  },
  computed: {
    packageStatusValue: {
      get() {
        return this.packageStatusOptions.find((o) => o.value === this.statusFilter) || null;
      },
      set(obj) {
        this.statusFilter = obj?.value ?? 'all';
      },
    },
    sortByValue: {
      get() {
        return this.sortByOptions.find((o) => o.value === this.sortBy) || null;
      },
      set(obj) {
        this.sortBy = obj?.value ?? '';
      },
    },
    searchParams() {
      const payload = {
        page: this.paging.page,
        size: this.paging.pageSize,
        ...(this.searchQuery ? { name: this.searchQuery } : {}),
        ...(this.statusFilter !== 'all' ? { status: this.statusFilter === 'active' ? 1 : this.statusFilter === 'inactive' ? 2 : 0 } : {}),
        ...(this.sortBy ? { SortBy: this.sortBy } : {}),
      };
      const params = new URLSearchParams();
      for (const key in payload) {
        params.append(key, payload[key]);
      }
      return params;
    },
  },
  watch: {
    'paging.page'() {
      this.loadPackages();
    },
    'paging.pageSize'() {
      this.paging.page = 1;
      this.loadPackages();
    },
  },
  mounted() {
    this.loadPackages();
  },
  activated() {
    this.loadPackages();
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },
  methods: {
    handleSearchDebounce() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.paging.page = 1;
        this.loadPackages();
      }, 2000);
    },
    handleSearch() {
      this.paging.page = 1;
      this.loadPackages();
    },
    resetFilters() {
      this.searchQuery = '';
      this.statusFilter = 'all';
      this.sortBy = '';
      this.paging.page = 1;
      this.loadPackages();
    },
    async loadPackages() {
      try {
        this.loading = true;
        const res = await this.$api.get('SalePackage', { params: this.searchParams });
        const raw = res?.data ?? res;
        if (Array.isArray(raw)) {
          this.packages = raw;
          this.paging.total = raw.length;
        } else if (Array.isArray(raw?.items)) {
          this.packages = raw.items;
          this.paging.total = raw.total ?? raw.items.length;
        } else if (Array.isArray(raw?.data)) {
          this.packages = raw.data;
          this.paging.total = raw.total ?? raw.data.length;
        } else {
          this.packages = [];
          this.paging.total = 0;
        }
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải danh sách gói dịch vụ' });
      } finally {
        this.loading = false;
      }
    },
    formatPrice(price) {
      if (!price && price !== 0) return '0';
      return new Intl.NumberFormat('vi-VN').format(price);
    },
    calcDiscount(originalPrice, salePrice) {
      if (!originalPrice || !salePrice || originalPrice <= 0) return 0;
      return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      const ms = typeof timestamp === 'number' && timestamp < 1e12
        ? timestamp * 1000
        : timestamp;
      return this.$moment(ms).format('DD/MM/YYYY');
    },
    createNewPackage() {
      this.$router.push({ name: 'package_management_create' });
    },
    viewPackage(pkg) {
      this.$router.push({
        name: 'package_management_view',
        params: { id: pkg.id },
      });
    },
    editPackage(pkg) {
      this.$router.push({
        name: 'package_management_edit',
        params: { id: pkg.id },
      });
    },
  },
};
</script>

<style scoped>
.package-management {
  padding: 0;
}

.filter-bar {
  padding: 16px 20px;

  ::v-deep .form-group {
    margin-bottom: 0;
    label {
      display: none;
    }
  }

  .filter-col-input {
    display: flex;
    align-items: center;
  }
}

.status-draft    { background: #f3f4f8; color: #7e8299; }
.status-active   { background: #e8fff3; color: #50cd89; }
.status-inactive { background: #fff5f8; color: #f1416c; }
.status-badge {
  display: inline-block;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 0.82rem;
  font-weight: 600;
}

.search-input {
  padding-left: 40px;
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  height: 44px;
}

.search-icon {
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a5b7;
  z-index: 5;
}

.filter-select {
  border-radius: 8px;
  border: 1px solid #e1e5e9;
  height: 44px;
}

.package-card {
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.02);
}

.package-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 25px 0 rgba(76, 87, 125, 0.1);
}

.badge-circle {
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 12px;
}

.badge-light-success {
  background-color: #e8fff3;
  color: #50cd89;
}

.badge-light-warning {
  background-color: #fff8dd;
  color: #f6c000;
}

.card-title {
  font-size: 1.4rem;
  line-height: 1.4;
}

.price-current {
  font-size: 1.5rem;
}

.price-original {
  margin-left: 6px;
  text-decoration: line-through;
  font-size: 1rem;
}

.discount-badge {
  font-size: 0.8rem;
  border-radius: 6px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.bg-light-success {
  background-color: #e8fff3 !important;
}

.stat-item h4 {
  font-size: 1.5rem;
  margin-bottom: 4px;
}

.stat-item span {
  font-size: 0.9rem;
}

.btn {
  border-radius: 8px;
  padding: 10px 16px;
}

.btn-light {
  background-color: #f9f9f9;
  border-color: #e1e5e9;
  color: #5e6278;
}

.btn-light:hover {
  background-color: #f1f1f2;
  border-color: #d3d3da;
  color: #5e6278;
}

.empty-state {
  padding: 60px 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
  .row.mb-6 [class*="col-"] {
    margin-bottom: 1rem;
  }
}

/* Success color theme */
.text-success {
  color: #50cd89 !important;
}

.badge-light-success .fas {
  font-size: 10px;
}

.page-size-select {
  border-color: #dee2e6;
  font-size: 0.875rem;
}
::v-deep .pagination-custom .page-item.active .page-link {
  background-color: #01645A;
  border-color: #01645A;
  color: #fff;
}
::v-deep .pagination-custom .page-item .page-link {
  color: #21a567;
}
::v-deep .pagination-custom .page-item .page-link:hover {
  background-color: #e8f8f0;
  border-color: #21a567;
  color: #21a567;
}
</style>